import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  People,
  PersonAdd,
  MailOutline,
  AdminPanelSettings,
} from '@mui/icons-material';
import { userService, User } from '../services/userService';
import { roleService } from '../services/roleService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiCollection, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

type UserFilter = 'all' | 'licensed' | 'guest' | 'mailbox' | 'admin';

export const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<UserFilter>(
    (searchParams.get('filter') as UserFilter) || 'all'
  );
  const [globalAdminIds, setGlobalAdminIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const navigate = useNavigate();

  useEffect(() => {
    const filterParam = searchParams.get('filter') as UserFilter;
    if (filterParam && ['all', 'licensed', 'guest', 'mailbox', 'admin'].includes(filterParam)) {
      setFilter(filterParam);
    }
  }, [searchParams]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersResponse, adminsResponse] = await Promise.all([
        userService.getUsers({ top: 999 }),
        roleService.getGlobalAdministrators(),
      ]);

      const users = getApiCollection<User>(usersResponse, 'loading users');
      setUsers(users);

      const adminUsers = getApiCollection<any>(adminsResponse, 'loading global administrators');
      const adminIds = new Set<string>(
        adminUsers.map((admin: any) => getProp(admin, 'id') as string)
      );
      setGlobalAdminIds(adminIds);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const getFilterCounts = useMemo(() => {
    const licensed = users.filter(
      (user: any) => getProp(user, 'assignedLicenses')?.length > 0
    ).length;
    const guests = users.filter((user: any) => {
      const userType = getProp(user, 'userType');
      const upn = getProp(user, 'userPrincipalName');
      return userType === 'Guest' || upn?.includes('#EXT#');
    }).length;
    const mailboxes = users.filter((user: any) => {
      const licenses = getProp(user, 'assignedLicenses') || [];
      const userType = getProp(user, 'userType');
      const upn = getProp(user, 'userPrincipalName') || '';
      return (
        licenses.length === 0 &&
        getProp(user, 'mail') &&
        userType !== 'Guest' &&
        !upn.includes('#EXT#')
      );
    }).length;
    const admins = users.filter((user: any) => globalAdminIds.has(getProp(user, 'id'))).length;

    return { licensed, guests, mailboxes, admins };
  }, [users, globalAdminIds]);

  const filteredUsers = useMemo(() => {
    if (filter === 'all') return users;

    return users.filter((user: any) => {
      const licenses = getProp(user, 'assignedLicenses') || [];
      const userType = getProp(user, 'userType');
      const upn = getProp(user, 'userPrincipalName') || '';
      const mail = getProp(user, 'mail');

      switch (filter) {
        case 'licensed':
          return licenses.length > 0;
        case 'guest':
          return userType === 'Guest' || upn.includes('#EXT#');
        case 'mailbox':
          return licenses.length === 0 && mail && userType !== 'Guest' && !upn.includes('#EXT#');
        case 'admin':
          return globalAdminIds.has(getProp(user, 'id'));
        default:
          return true;
      }
    });
  }, [users, filter, globalAdminIds]);

  const paginatedUsers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={styles.pageHeader}>
        <Typography variant="h4">Users</Typography>
        <Button variant="contained" startIcon={<AddIcon />}>
          Create User
        </Button>
      </Box>

      <Box sx={styles.filterSection}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, newFilter) => newFilter && setFilter(newFilter)}
          aria-label="user filter"
        >
          <ToggleButton value="all" aria-label="all users">
            <People sx={styles.iconMargin} />
            All ({users.length})
          </ToggleButton>
          <ToggleButton value="licensed" aria-label="licensed users">
            <People sx={styles.iconMargin} />
            Licensed ({getFilterCounts.licensed})
          </ToggleButton>
          <ToggleButton value="guest" aria-label="guest users">
            <PersonAdd sx={styles.iconMargin} />
            Guests ({getFilterCounts.guests})
          </ToggleButton>
          <ToggleButton value="mailbox" aria-label="shared mailboxes">
            <MailOutline sx={styles.iconMargin} />
            Shared Mailboxes ({getFilterCounts.mailboxes})
          </ToggleButton>
          <ToggleButton value="admin" aria-label="global admin users">
            <AdminPanelSettings sx={styles.iconMargin} />
            Global Admins ({getFilterCounts.admins})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={styles.errorAlert}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={styles.tableHead}>
            <TableRow>
              <TableCell sx={styles.tableHeaderCell}>Display Name</TableCell>
              <TableCell sx={styles.tableHeaderCell}>User Principal Name</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Job Title</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Department</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Licenses</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No users matched the current filter. If you expected results, check the filter or
                  API permissions.
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user: any) => {
                const licenses = getProp(user, 'assignedLicenses') || [];
                const userType = getProp(user, 'userType');
                const upn = getProp(user, 'userPrincipalName') || '';
                const isGuest = userType === 'Guest' || upn.includes('#EXT#');
                const userId = getProp(user, 'id');

                return (
                  <TableRow
                    key={userId}
                    hover
                    sx={styles.tableRow}
                    onClick={() => navigate(`/users/${userId}`)}
                  >
                    <TableCell>
                      <Box sx={styles.flexCenter}>
                        {getProp(user, 'displayName')}
                        {isGuest && <Chip label="Guest" size="small" color="info" />}
                      </Box>
                    </TableCell>
                    <TableCell>{upn}</TableCell>
                    <TableCell>{getProp(user, 'jobTitle') || '-'}</TableCell>
                    <TableCell>{getProp(user, 'department') || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={licenses.length}
                        color={licenses.length > 0 ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getProp(user, 'accountEnabled') ? 'Enabled' : 'Disabled'}
                        color={getProp(user, 'accountEnabled') ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};
